import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import TextBox from 'components/common/text_box'
import {
  normalizeDate,
  shortDateInputMaskParts,
  shortDatePatternLabel,
  formatDateShort,
} from 'client/griffin'

class DateTextBox extends React.Component {
  static propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    timeZone: PropTypes.string.isRequired,
    isMobile: PropTypes.bool,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    active: PropTypes.bool,
  }

  static defaultProps = {
    timeZone: 'Etc/UTC',
  }

  handleValueEvent = (e, done = false) => {
    const { isMobile, timeZone } = this.props

    if (isMobile) {
      if (e.target.value === '') {
        return ''
      }
      // Mobile provides a date picker so do not need validation
      return new Date(e.target.value)
    }

    const value = e.target.value.trim()
    // legal date, convert to date object using griffin
    let dateValue = normalizeDate(value, { timeZone })
    if (done) {
      if (!dateValue.errors) {
        return dateValue
      } else {
        dateValue = normalizeDate(value, { timeZone, style: 'yMd' })
        if (!dateValue.errors) {
          return dateValue
        }
      }
    }

    // illegal date, probably because the user is in the middle of typing,
    // just pass the string as is
    return e.target.value
  }

  handleChange = e => {
    if (this.props.onChange) {
      this.props.onChange(this.handleValueEvent(e))
    }
  }

  handleBlur = e => {
    if (this.props.onBlur) {
      this.props.onBlur(this.handleValueEvent(e, true))
    }
  }

  addZeroesToDate = value => {
    return value.replace(/\b(\d)\b/g, '0$1')
  }

  // This function returns the mask for the date text box, based on what the user has typed so far
  // if adaptToInput is true, we should adapt to the user's input, for example is if the user typed
  // 1/1/18, the mask should be [\d, /, \d, /, \d, \d]
  mask = (adaptToInput, rawValue) => {
    const tokenizationRx = /(\b\d+\b)|[^\d]+/g

    // tokenize the user's input so that we'll have an easier time parsing it
    let rawTokens = rawValue.match(tokenizationRx)
    let mask = []
    const maskParts = shortDateInputMaskParts()
    maskParts.forEach((maskPart, maskIndex) => {
      // this stores the length of what the user has entered, for example if the user entered 1/1/18, token length for the first
      // part (excluding literals) is 1, second part is 1 and third part is 2
      const tokenLength =
        rawTokens && rawTokens[maskIndex] && rawTokens[maskIndex].length
      if (maskPart.type === 'literal') {
        mask.push(maskPart.value)
      } else if (maskPart.type === 'day') {
        // if we encountered the day of the mask, and we need to adapt to the input, add one digit, if needed,
        // else add two
        if (adaptToInput && tokenLength === 1) {
          mask.push(/\d/)
        } else {
          mask.push(/\d/, /\d/)
        }
      } else if (maskPart.type === 'month') {
        // if we encountered the month of the mask, and we need to adapt to the input, add one digit, if needed,
        // else add two
        if (adaptToInput && tokenLength === 1) {
          mask.push(/\d/)
        } else {
          mask.push(/\d/, /\d/)
        }
      } else if (maskPart.type === 'year') {
        // if we encountered the year of the mask, and we need to adapt to the input, add two digits, if needed,
        // else add four
        if (adaptToInput && tokenLength === 2) {
          mask.push(/\d/, /\d/)
        } else {
          mask.push(/\d/, /\d/, /\d/, /\d/)
        }
      }
    })
    return mask
  }

  render() {
    let { value } = this.props
    // if we're on mobile, use the native input[type=date]
    if (this.props.isMobile) {
      if (this.props.value) {
        // This is the format that the browser expects for input[type='date']
        value = isNaN(this.props.value.getTime())
          ? ''
          : new Date(this.props.value).toISOString().split('T')[0]
      } else {
        value = ''
      }

      return (
        <TextBox
          placeholder={shortDatePatternLabel()}
          type="date"
          {...this.props}
          onChange={this.handleChange}
          onBlur={this.handleBlur}
          value={value}
        />
      )
    }

    if (this.props.value instanceof Date) {
      value = formatDateShort(this.props.value)
      if (this.props.active) {
        value = this.addZeroesToDate(value)
      }
    }

    return (
      <TextBox
        mask={this.mask.bind(this, this.props.value instanceof Date)}
        keepCharPositions={true}
        guide={false}
        placeholder={shortDatePatternLabel()}
        {...this.props}
        value={value}
        onChange={this.handleChange}
        onBlur={this.handleBlur}
      />
    )
  }
}

export default connect(state => ({
  isMobile: state.device_info.isMobile,
  timeZone: state.locality && state.locality.timezone,
}))(DateTextBox)
